/**
 * useMockGridData - Hook for managing mock grid data
 *
 * Provides mock data and update functionality for testing
 * the UnifiedDataGrid prototype without hitting real APIs.
 */

import { useState, useEffect, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  MOCK_ROWS,
  MOCK_USERS,
  MOCK_CONTACTS,
  MOCK_ORGANIZATIONS,
  STATUS_OPTIONS,
  TYPE_OPTIONS,
  VERTICAL_OPTIONS,
  MockRow,
} from './testData'

interface BatchUpdatePayload {
  tableName: string
  PK: string
  SK: string
  fieldName: string
  newValue: any
}

interface UseMockGridDataOptions {
  /** Initial rows (defaults to MOCK_ROWS) */
  initialRows?: MockRow[]
  /** Cache key for React Query */
  cacheKey?: string[]
}

export const useMockGridData = (options: UseMockGridDataOptions = {}) => {
  const { initialRows = MOCK_ROWS, cacheKey = ['test-grid-data'] } = options

  const [rows, setRows] = useState<MockRow[]>(initialRows)
  const queryClient = useQueryClient()

  // Seed React Query cache with mock data on mount
  useEffect(() => {
    queryClient.setQueryData(cacheKey, {
      rows,
      users: MOCK_USERS,
      contacts: MOCK_CONTACTS,
      organizations: MOCK_ORGANIZATIONS,
    })
  }, [queryClient, cacheKey, rows])

  /**
   * Apply batch updates to local state
   * This simulates what the real API would do
   */
  /**
   * Resolve contact UUIDs to contact objects
   */
  const resolveContacts = (uuids: string[]): typeof MOCK_CONTACTS => {
    return uuids
      .map(uuid => MOCK_CONTACTS.find(c => c.contacts_uuid === uuid))
      .filter(Boolean) as typeof MOCK_CONTACTS
  }

  /**
   * Resolve org UUIDs to org objects
   */
  const resolveOrgs = (uuids: string[]): typeof MOCK_ORGANIZATIONS => {
    return uuids
      .map(uuid => MOCK_ORGANIZATIONS.find(o => o.org_uuid === uuid))
      .filter(Boolean) as typeof MOCK_ORGANIZATIONS
  }

  const applyUpdates = useCallback(
    (updates: BatchUpdatePayload[], normalizedFields: Record<string, any>) => {
      // Build lookup set for which rows to update
      const updateKeys = new Set(
        updates.map(u => `${u.PK}::${u.SK}`)
      )

      // Contact field names that need resolution
      const contactFields = ['primaryContact', 'billingContact', 'contacts']
      // Org reference field names that need resolution
      const orgRefFields = ['contractedBy', 'affiliated']

      setRows(prevRows =>
        prevRows.map(row => {
          const rowKey = `${row.PK}::${row.SK}`
          if (updateKeys.has(rowKey)) {
            // Apply normalized fields
            const updatedRow = { ...row }

            for (const [field, value] of Object.entries(normalizedFields)) {
              // Handle nested fields like 'team.AM'
              if (field.includes('.')) {
                const [parent, child] = field.split('.')
                ;(updatedRow as Record<string, any>)[parent] = {
                  ...((updatedRow as Record<string, any>)[parent] || {}),
                  [child]: value,
                }

                // Also update _resolvedTeam if it's a team field
                if (parent === 'team' && updatedRow._resolvedTeam) {
                  const resolvedUsers = (value as string[])
                    .map(uuid => MOCK_USERS.find(u => u.user_uuid === uuid))
                    .filter(Boolean) as typeof MOCK_USERS

                  updatedRow._resolvedTeam = {
                    ...updatedRow._resolvedTeam,
                    [child]: resolvedUsers,
                  }
                }
              } else if (contactFields.includes(field)) {
                // Resolve contact UUIDs to objects
                const uuids = Array.isArray(value) ? value : [value].filter(Boolean)
                const resolved = resolveContacts(uuids)
                // Store both the UUIDs and resolved objects
                ;(updatedRow as Record<string, any>)[field] = resolved.length === 1 ? resolved[0] : resolved
              } else if (orgRefFields.includes(field)) {
                // Resolve org UUIDs to objects
                const uuids = Array.isArray(value) ? value : [value].filter(Boolean)
                ;(updatedRow as Record<string, any>)[field] = resolveOrgs(uuids)
              } else if (field === 'owner') {
                // Resolve owner UUID to user object
                const uuid = Array.isArray(value) ? value[0] : value
                const user = MOCK_USERS.find(u => u.user_uuid === uuid)
                ;(updatedRow as Record<string, any>)[field] = user || value
              } else {
                (updatedRow as Record<string, any>)[field] = value
              }
            }

            return updatedRow
          }
          return row
        })
      )

      // Update React Query cache as well (simplified - state update above is source of truth)
      queryClient.setQueryData(cacheKey, (oldData: any) => ({
        ...oldData,
        // The rows state will sync via the useEffect, but we update here for consistency
      }))
    },
    [queryClient, cacheKey]
  )

  /**
   * Delete rows by IDs
   */
  const deleteRows = useCallback(
    (ids: Set<string | number>) => {
      const idStrings = new Set(Array.from(ids).map(String))

      setRows(prevRows =>
        prevRows.filter(row => !idStrings.has(row.id) && !idStrings.has(row.org_uuid))
      )

      queryClient.setQueryData(cacheKey, (oldData: any) => ({
        ...oldData,
        rows: oldData?.rows?.filter(
          (row: MockRow) => !idStrings.has(row.id) && !idStrings.has(row.org_uuid)
        ),
      }))
    },
    [queryClient, cacheKey]
  )

  /**
   * Reset to initial data
   */
  const reset = useCallback(() => {
    setRows(initialRows)
    queryClient.setQueryData(cacheKey, {
      rows: initialRows,
      users: MOCK_USERS,
      contacts: MOCK_CONTACTS,
      organizations: MOCK_ORGANIZATIONS,
    })
  }, [initialRows, queryClient, cacheKey])

  return {
    // Data
    rows,
    users: MOCK_USERS,
    contacts: MOCK_CONTACTS,
    organizations: MOCK_ORGANIZATIONS,

    // Options for dropdowns
    statusOptions: STATUS_OPTIONS,
    typeOptions: TYPE_OPTIONS,
    verticalOptions: VERTICAL_OPTIONS,

    // Methods
    applyUpdates,
    deleteRows,
    reset,
    setRows,
  }
}

export default useMockGridData
